/**
 * API Ingest Chat Component
 *
 * Chat-first conversational interface for setting up external API integrations.
 * Supports:
 *   - Natural language input ("Set up Xero API")
 *   - PDF/file drag-and-drop
 *   - URL detection and auto-fetch
 *   - Inline credential collection forms
 *   - Workflow progress tracking
 *   - Session persistence for resume
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  TextField,
  IconButton,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  Stack,
  CircularProgress,
  Alert,
  LinearProgress,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Collapse,
} from '@mui/material';
import {
  Send as SendIcon,
  AutoAwesome as SparklesIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  HourglassEmpty as PendingIcon,
  PlayArrow as PlayIcon,
  UploadFile as UploadFileIcon,
  Link as LinkIcon,
  Api as ApiIcon,
  Replay as RetryIcon,
  CloudUpload as CloudUploadIcon,
  ContentCopy as CopyIcon,
  OpenInNew as OpenInNewIcon,
  VpnKey as VpnKeyIcon,
  LinkOff as LinkOffIcon,
} from '@mui/icons-material';
import { apiManagerClient, apiOAuthApi } from './api';
import { buildIngestOauthRedirectUri } from './ingestCredentialForm';
import apiManagerApi from './api';

// ============================================================================
// Types
// ============================================================================

interface ApiIngestMessage {
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: Date;
  phase?: string;
  next_action?: string;
  auth_type?: string;
  api_definition?: any;
  endpoints?: any[];
  pipeline_id?: string;
  steps_completed?: string[];
  steps_remaining?: string[];
  error?: string;
  attachment?: { type: string; filename?: string; url?: string };
  auth_setup_instructions?: string[];
  important_notes?: string[];
  required_credentials?: any;
  api_id?: string;
  oauth_config?: any;
  candidates?: ApiCandidate[];
  selected_candidate?: ApiCandidate | null;
  all_mappings?: Array<{
    endpoint_id?: string;
    mappings?: Array<{ external_field?: string; iku_field?: string; confirmed?: boolean }>;
  }>;
  catalog_mappings_by_endpoint?: Array<{
    endpoint_id?: string;
    mappings?: Array<{ external_field?: string; iku_field?: string; confirmed?: boolean }>;
  }>;
}

interface ApiCandidate {
  candidate_id: string;
  name: string;
  provider?: string;
  docs_url?: string | null;
  spec_url?: string | null;
  auth_type?: string;
  confidence?: number;
  why?: string;
  base_url_hint?: string | null;
}

// ============================================================================
// Known OAuth2 provider presets
// ============================================================================
const OAUTH_PRESETS: Record<string, { label: string; authorization_url: string; token_url: string; scopes: string[] }> = {
  john_deere: {
    label: 'John Deere',
    authorization_url: 'https://signin.johndeere.com/oauth2/aus78tnlaysMraFhC1t7/v1/authorize',
    token_url: 'https://signin.johndeere.com/oauth2/aus78tnlaysMraFhC1t7/v1/token',
    scopes: ['ag1', 'ag2', 'ag3', 'eq1', 'eq2', 'files', 'offline_access'],
  },
  case_ih: {
    // Same CNH Identity endpoints as services/shared/api-ingest-provider-catalog.js (cnh_afs_connect).
    label: 'Case IH / CNH',
    authorization_url: 'https://identity.cnhind.com/oauth2/default/v1/authorize',
    token_url: 'https://identity.cnhind.com/oauth2/default/v1/token',
    scopes: ['telemetry', 'equipment'],
  },
};

interface ApiIngestChatProps {
  sessionId: string | null;
  onSessionChange: (sessionId: string | null) => void;
  onPipelineReady: (pipelineId: string) => void;
  /** When set, the chat will auto-load the session for this API (used by Configure button) */
  resumeApiId?: string | null;
}

// ============================================================================
// Helper: URL detection
// ============================================================================

const URL_REGEX = /https?:\/\/[^\s<>"{}|\\^`\[\]]+/gi;

function detectUrls(text: string): string[] {
  return (text.match(URL_REGEX) || []);
}

// ============================================================================
// Workflow Progress Component
// ============================================================================

function WorkflowProgress({ completed = [], remaining = [] }: { completed: string[]; remaining: string[] }) {
  const total = completed.length + remaining.length;
  if (total === 0) return null;
  const pct = Math.round((completed.length / total) * 100);

  return (
    <Box sx={{ my: 1 }}>
      <LinearProgress variant="determinate" value={pct} sx={{ mb: 1, borderRadius: 1, height: 6 }} />
      <Stack spacing={0.5}>
        {completed.map((step, i) => (
          <Box key={`c-${i}`} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <CheckIcon sx={{ fontSize: 16, color: 'success.main' }} />
            <Typography variant="caption" color="text.secondary">{step}</Typography>
          </Box>
        ))}
        {remaining.map((step, i) => (
          <Box key={`r-${i}`} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {i === 0 ? (
              <CircularProgress size={14} sx={{ color: 'primary.main' }} />
            ) : (
              <PendingIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
            )}
            <Typography variant="caption" color={i === 0 ? 'primary.main' : 'text.disabled'}>{step}</Typography>
          </Box>
        ))}
      </Stack>
    </Box>
  );
}

// ============================================================================
// Credential Collection Form (branches by auth type)
// ============================================================================

function CredentialForm({
  authType,
  onSubmit,
  loading,
  apiId,
  oauthConfig,
  sessionId,
  onOAuthComplete,
}: {
  authType: string;
  onSubmit: (creds: any) => void;
  loading: boolean;
  apiId?: string;
  oauthConfig?: any;
  sessionId?: string | null;
  onOAuthComplete?: () => void;
}) {
  // Per-app BYOK (api_app_connections) requires an x-app-id; the standalone
  // API Manager chat has no app context, so tenant-shared is the only mode
  // that can succeed here.
  const [credentialMode, setCredentialMode] = useState<'byok_app' | 'shared_tenant'>('shared_tenant');
  const [oauthError, setOauthError] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [bearer, setBearer] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [hmacPublicKey, setHmacPublicKey] = useState('');
  const [hmacPrivateKey, setHmacPrivateKey] = useState('');
  const [oauthClientId, setOauthClientId] = useState('');
  const [oauthClientSecret, setOauthClientSecret] = useState('');
  const [oauthTokenUrl, setOauthTokenUrl] = useState('');
  const [oauthAuthUrl, setOauthAuthUrl] = useState('');
  const [oauthScopes, setOauthScopes] = useState('');
  const [oauthProvider, setOauthProvider] = useState('generic');
  const [oauthStatus, setOauthStatus] = useState<'idle' | 'waiting' | 'connected' | 'error'>('idle');
  const [validationError, setValidationError] = useState('');
  const pollRef = useRef<any>(null);

  // Apply preset when provider changes
  const applyPreset = (key: string) => {
    setOauthProvider(key);
    const preset = OAUTH_PRESETS[key];
    if (preset) {
      setOauthAuthUrl(preset.authorization_url);
      setOauthTokenUrl(preset.token_url);
      setOauthScopes(preset.scopes.join(', '));
    }
  };

  // Copy redirect URI to clipboard
  const redirectUri = typeof window !== 'undefined'
    ? buildIngestOauthRedirectUri(window.location.origin)
    : '';
  const copyRedirectUri = () => {
    navigator.clipboard?.writeText(redirectUri);
  };

  // Validate before submit
  const validate = (): boolean => {
    setValidationError('');
    if (authType === 'api_key' && !apiKey.trim()) {
      setValidationError('API Key is required'); return false;
    }
    if (authType === 'bearer' && !bearer.trim()) {
      setValidationError('Bearer token is required'); return false;
    }
    if (authType === 'basic' && (!username.trim() || !password.trim())) {
      setValidationError('Username and password are required'); return false;
    }
    if (authType === 'hmac' && (!hmacPublicKey.trim() || !hmacPrivateKey.trim())) {
      setValidationError('HMAC public key and private key are required'); return false;
    }
    if (authType === 'oauth2') {
      if (!oauthClientId.trim() || !oauthClientSecret.trim()) {
        setValidationError('Client ID and Client Secret are required'); return false;
      }
    }
    return true;
  };

  const handleSubmit = () => {
    if (!validate()) return;

    if (authType === 'api_key') {
      // BYOK app connection: store secrets in api_app_connections, then proceed using stored creds.
      if (credentialMode === 'byok_app' && apiId) {
        apiManagerApi.upsertAppConnection({
          api_id: apiId,
          auth_type: 'api_key',
          credentials: { auth_type: 'api_key', api_key: apiKey },
        })
          .then(() => onSubmit({ auth_type: 'api_key', from_app_connection: true }))
          .catch((e: any) => setValidationError(e?.response?.data?.error || e?.message || 'Failed to save app credentials'));
        return;
      }
      // Shared tenant creds (legacy): send inline so backend can persist to external_apis.
      onSubmit({ auth_type: 'api_key', api_key: apiKey });
    } else if (authType === 'bearer') {
      if (credentialMode === 'byok_app' && apiId) {
        apiManagerApi.upsertAppConnection({
          api_id: apiId,
          auth_type: 'bearer',
          credentials: { auth_type: 'bearer', token: bearer },
        })
          .then(() => onSubmit({ auth_type: 'bearer', from_app_connection: true }))
          .catch((e: any) => setValidationError(e?.response?.data?.error || e?.message || 'Failed to save app credentials'));
        return;
      }
      onSubmit({ auth_type: 'bearer', token: bearer });
    } else if (authType === 'basic') {
      if (credentialMode === 'byok_app' && apiId) {
        apiManagerApi.upsertAppConnection({
          api_id: apiId,
          auth_type: 'basic',
          credentials: { auth_type: 'basic', username, password },
        })
          .then(() => onSubmit({ auth_type: 'basic', from_app_connection: true }))
          .catch((e: any) => setValidationError(e?.response?.data?.error || e?.message || 'Failed to save app credentials'));
        return;
      }
      onSubmit({ auth_type: 'basic', username, password });
    } else if (authType === 'hmac') {
      const hmacPayload = {
        auth_type: 'hmac',
        public_key: hmacPublicKey,
        private_key: hmacPrivateKey,
        hmac_config: {
          public_key: hmacPublicKey,
          private_key: hmacPrivateKey,
        },
      };
      if (credentialMode === 'byok_app' && apiId) {
        apiManagerApi.upsertAppConnection({
          api_id: apiId,
          auth_type: 'hmac',
          credentials: hmacPayload,
        })
          .then(() => onSubmit({ auth_type: 'hmac', from_app_connection: true }))
          .catch((e: any) => setValidationError(e?.response?.data?.error || e?.message || 'Failed to save app credentials'));
        return;
      }
      onSubmit(hmacPayload);
    } else if (authType === 'oauth2') {
      // Submit app config (no access_token) -> backend will set awaiting_oauth
      onSubmit({
        auth_type: 'oauth2',
        provider: oauthProvider,
        client_id: oauthClientId,
        client_secret: oauthClientSecret,
        token_url: oauthTokenUrl || undefined,
        authorization_url: oauthAuthUrl || undefined,
        scopes: oauthScopes ? oauthScopes.split(/[,\s]+/).filter(Boolean) : undefined,
      });
    }
  };

  // OAuth browser flow: start + poll
  const handleStartOAuth = async () => {
    if (!apiId) return;
    const clientId = String(oauthConfig?.client_id || oauthClientId || '').trim();
    const clientSecret = oauthClientSecret.trim(); // only in memory, never read back from server
    if (!clientId || !clientSecret) {
      setValidationError('Client ID and Client Secret are required to connect.');
      return;
    }
    setValidationError('');
    setOauthError('');
    setOauthStatus('waiting');
    try {
      const resp = await apiOAuthApi.start({
        api_id: apiId,
        provider: oauthConfig?.provider || oauthProvider || 'generic',
        client_id: clientId,
        client_secret: clientSecret,
        authorization_url: oauthConfig?.authorization_url || oauthAuthUrl || undefined,
        token_url: oauthConfig?.token_url || oauthTokenUrl || undefined,
        scopes: oauthConfig?.scopes || (oauthScopes ? oauthScopes.split(/[,\s]+/).filter(Boolean) : undefined),
        redirect_uri: redirectUri,
      });
      const authUrl = resp.data?.authorization_url;
      if (authUrl) {
        window.open(authUrl, '_blank', 'noopener,noreferrer');
        // Start polling auth-status
        startPolling();
      } else if (resp.data?.saved) {
        // client_credentials: stored server-side, no browser hop.
        startPolling();
      } else {
        setOauthError('No authorization URL was returned for this provider.');
        setOauthStatus('error');
      }
    } catch (e: any) {
      setOauthError(e?.response?.data?.error || e?.response?.data?.message || e?.message || 'OAuth start failed');
      setOauthStatus('error');
    }
  };

  const startPolling = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      if (!apiId) return;
      try {
        const resp = await apiManagerClient.get(`/cp/api-ingest/auth-status/${encodeURIComponent(apiId)}`);
        const d = resp.data;
        if (d?.has_tokens && d?.status === 'active') {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setOauthStatus('connected');
          // Notify chat to advance session
          if (sessionId) {
            try {
              await apiManagerClient.post('/cp/api-ingest/oauth-complete', { session_id: sessionId });
            } catch { /* best-effort */ }
          }
          onOAuthComplete?.();
        }
      } catch { /* keep polling */ }
    }, 3000);
  };

  // Cleanup polling on unmount
  useEffect(() => {
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  const isOAuth = authType === 'oauth2';

  return (
    <Card variant="outlined" sx={{ my: 1, bgcolor: isOAuth ? 'info.50' : 'warning.50', borderColor: isOAuth ? 'info.light' : 'warning.main' }}>
      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <VpnKeyIcon sx={{ fontSize: 18, color: isOAuth ? 'info.main' : 'warning.main' }} />
          <Typography variant="subtitle2">
            {authType === 'api_key' && 'Enter API Key'}
            {authType === 'bearer' && 'Enter Bearer Token'}
            {authType === 'basic' && 'Enter Credentials'}
            {authType === 'hmac' && 'Enter HMAC Keys'}
            {authType === 'oauth2' && 'Connect Your Account (OAuth2)'}
          </Typography>
        </Box>

        {validationError && (
          <Alert severity="error" sx={{ mb: 1, py: 0 }} onClose={() => setValidationError('')}>
            {validationError}
          </Alert>
        )}

        {!isOAuth && (
          <Box sx={{ mb: 1.5 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Credential mode
            </Typography>
            <RadioGroup
              row
              value={credentialMode}
              onChange={(e) => setCredentialMode(e.target.value as any)}
            >
              <FormControlLabel
                value="byok_app"
                control={<Radio size="small" />}
                label="Store for this app (BYOK, needs an app context)"
                disabled
              />
              <FormControlLabel
                value="shared_tenant"
                control={<Radio size="small" />}
                label="Use tenant shared creds"
              />
            </RadioGroup>
            {credentialMode === 'shared_tenant' && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                Shared mode stores credentials tenant-wide (platform-sponsored). If shared creds already exist, you can continue without re-entering them.
              </Alert>
            )}
          </Box>
        )}

        {authType === 'api_key' && (
          <TextField
            label="API Key"
            type="password"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            fullWidth
            size="small"
            required
            error={!!validationError}
          />
        )}

        {authType === 'bearer' && (
          <TextField
            label="Bearer Token"
            type="password"
            value={bearer}
            onChange={(e) => setBearer(e.target.value)}
            fullWidth
            size="small"
            required
            error={!!validationError}
          />
        )}

        {authType === 'basic' && (
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
            <TextField label="Username" value={username} onChange={(e) => setUsername(e.target.value)} size="small" required />
            <TextField label="Password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} size="small" required />
          </Box>
        )}

        {authType === 'hmac' && (
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
            <TextField
              label="Public Key"
              value={hmacPublicKey}
              onChange={(e) => setHmacPublicKey(e.target.value)}
              size="small"
              required
            />
            <TextField
              label="Private Key"
              type="password"
              value={hmacPrivateKey}
              onChange={(e) => setHmacPrivateKey(e.target.value)}
              size="small"
              required
            />
          </Box>
        )}

        {isOAuth && oauthStatus !== 'connected' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {/* Provider presets */}
            {Object.keys(OAUTH_PRESETS).length > 0 && oauthStatus === 'idle' && (
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                  Quick presets:
                </Typography>
                <Stack direction="row" spacing={0.5} flexWrap="wrap">
                  {Object.entries(OAUTH_PRESETS).map(([key, p]) => (
                    <Chip
                      key={key}
                      label={p.label}
                      size="small"
                      variant={oauthProvider === key ? 'filled' : 'outlined'}
                      color={oauthProvider === key ? 'primary' : 'default'}
                      onClick={() => applyPreset(key)}
                    />
                  ))}
                  <Chip
                    label="Custom"
                    size="small"
                    variant={oauthProvider === 'generic' ? 'filled' : 'outlined'}
                    color={oauthProvider === 'generic' ? 'primary' : 'default'}
                    onClick={() => setOauthProvider('generic')}
                  />
                </Stack>
              </Box>
            )}

            {oauthStatus === 'idle' && (
              <>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                  <TextField label="Client ID" value={oauthClientId} onChange={(e) => setOauthClientId(e.target.value)} size="small" required />
                  <TextField label="Client Secret" type="password" value={oauthClientSecret} onChange={(e) => setOauthClientSecret(e.target.value)} size="small" required />
                  <TextField label="Authorization URL" value={oauthAuthUrl} onChange={(e) => setOauthAuthUrl(e.target.value)} size="small" placeholder="https://provider.com/oauth/authorize" />
                  <TextField label="Token URL" value={oauthTokenUrl} onChange={(e) => setOauthTokenUrl(e.target.value)} size="small" placeholder="https://provider.com/oauth/token" />
                  <TextField label="Scopes (comma separated)" value={oauthScopes} onChange={(e) => setOauthScopes(e.target.value)} size="small" sx={{ gridColumn: '1/-1' }} />
                </Box>

                {/* Redirect URI helper */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, bgcolor: 'grey.50', p: 1, borderRadius: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                    <strong>Redirect URI</strong> (add this to your provider app): <code style={{ fontSize: '0.7rem' }}>{redirectUri}</code>
                  </Typography>
                  <IconButton size="small" onClick={copyRedirectUri} title="Copy redirect URI">
                    <CopyIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Box>
              </>
            )}

            {oauthStatus === 'waiting' && (
              <Alert severity="info" icon={<CircularProgress size={18} />}>
                Waiting for you to approve access in the provider's window... This will update automatically.
              </Alert>
            )}

            {oauthStatus === 'error' && (
              <Alert severity="error">
                OAuth flow failed{oauthError ? `: ${oauthError}` : '. Please check your Client ID/Secret and try again.'}
              </Alert>
            )}
          </Box>
        )}

        {isOAuth && oauthStatus === 'connected' && (
          <Alert severity="success" icon={<CheckIcon />}>
            Account connected successfully! Proceeding with setup...
          </Alert>
        )}

        <Box sx={{ mt: 1.5, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          {/* For non-OAuth: simple Save & Continue */}
          {!isOAuth && (
            <Button
              variant="contained"
              size="small"
              onClick={handleSubmit}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={16} /> : <CheckIcon />}
            >
              Continue
            </Button>
          )}

          {/* If shared creds already exist, allow continuing without re-entering secrets */}
          {!isOAuth && credentialMode === 'shared_tenant' && apiId && (
            <Button
              variant="outlined"
              size="small"
              onClick={() => onSubmit({ auth_type: authType, from_shared_credentials: true })}
              disabled={loading}
              startIcon={<LinkOffIcon sx={{ fontSize: 18 }} />}
              title="Use existing shared credentials without storing new secrets"
            >
              Use Existing Shared
            </Button>
          )}

          {/* For OAuth: two-step (save config then connect) */}
          {isOAuth && oauthStatus === 'idle' && !apiId && (
            <Button
              variant="contained"
              size="small"
              onClick={handleSubmit}
              disabled={loading || !oauthClientId.trim() || !oauthClientSecret.trim()}
              startIcon={loading ? <CircularProgress size={16} /> : <VpnKeyIcon />}
            >
              Save App Config
            </Button>
          )}

          {isOAuth && oauthStatus === 'idle' && apiId && (
            <Button
              variant="contained"
              size="small"
              color="info"
              onClick={handleStartOAuth}
              disabled={loading}
              startIcon={<OpenInNewIcon />}
            >
              Connect Account
            </Button>
          )}

          {isOAuth && oauthStatus === 'error' && (
            <Button
              variant="outlined"
              size="small"
              color="error"
              onClick={() => setOauthStatus('idle')}
              startIcon={<RetryIcon />}
            >
              Retry
            </Button>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}

// ============================================================================
// Quick Action Cards (empty state)
// ============================================================================

function QuickActions({ onAction }: { onAction: (text: string) => void }) {
  const actions = [
    { icon: <ApiIcon />, label: 'Describe an API', example: 'Set up the Xero Accounting API', color: 'primary' as const },
    { icon: <LinkIcon />, label: 'OpenAPI/Swagger URL', example: 'https://petstore.swagger.io/v2/swagger.json', color: 'info' as const },
    { icon: <UploadFileIcon />, label: 'Upload PDF docs', example: 'Drop a PDF here', color: 'secondary' as const },
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 4, px: 2 }}>
      <SparklesIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
      <Typography variant="h6" textAlign="center">
        Set up a new API integration
      </Typography>
      <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ maxWidth: 400 }}>
        Tell me which API you want to connect, drop documentation, or paste an OpenAPI spec URL.
      </Typography>
      <Stack direction="row" spacing={1.5} sx={{ mt: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
        {actions.map((action, i) => (
          <Card
            key={i}
            variant="outlined"
            sx={{
              cursor: 'pointer',
              '&:hover': { borderColor: `${action.color}.main`, boxShadow: 1 },
              transition: 'all 0.2s',
              minWidth: 160,
            }}
            onClick={() => onAction(action.example)}
          >
            <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 }, textAlign: 'center' }}>
              <Box sx={{ color: `${action.color}.main`, mb: 0.5 }}>{action.icon}</Box>
              <Typography variant="caption" fontWeight={600}>{action.label}</Typography>
            </CardContent>
          </Card>
        ))}
      </Stack>
    </Box>
  );
}

// ============================================================================
// Main Chat Component
// ============================================================================

// Local step progress helper (mirrors backend PHASE_STEPS for poll result display)
const PHASE_STEPS_LOCAL: Record<string, string[]> = {
  discovering_candidates: ['Understanding intent', 'Searching for API docs', 'Ranking candidate APIs'],
  bootstrapping: ['Fetching selected API docs/spec', 'Parsing API definition', 'Creating templates', 'Registering API', 'Determining auth requirements'],
  discovering: ['Understanding intent', 'Searching for API docs', 'Fetching spec', 'Parsing API definition', 'Creating templates', 'Registering API', 'Determining auth requirements'],
  configuring: ['Configuring authentication', 'Creating field mappings'],
  testing: ['Running smoke test', 'Creating pipeline', 'Activating pipeline'],
};

function humanizeIngestError(raw: string) {
  const msg = String(raw || '').trim();
  const lower = msg.toLowerCase();
  if (!msg) return 'The last operation failed. Try again or choose a different API candidate.';
  if (lower.includes('not accessible from app context')) {
    return 'This API action is blocked by app access rules. Re-open API Ingest and try again, or ask an admin to allow this agent for your app.';
  }
  if (lower.includes('workflow') && lower.includes('not found')) {
    return 'The API ingest workflow is not registered yet. Refresh the page and retry in 30 seconds.';
  }
  if (lower.includes('timeout')) {
    return 'The API provider took too long to respond. Retry, or paste a direct OpenAPI/docs URL to speed up discovery.';
  }
  if (lower.includes('credentials') || lower.includes('oauth')) {
    return 'Credentials are incomplete. Complete the credential step and submit again.';
  }
  return `The last operation failed: ${msg}`;
}

function stepsProgressLocal(phase: string) {
  const completed: string[] = [];
  const remaining: string[] = [];
  let found = false;
  for (const [p, steps] of Object.entries(PHASE_STEPS_LOCAL)) {
    for (const s of steps) {
      if (p === phase && !found) { found = true; remaining.push(s); }
      else if (found) { remaining.push(s); }
      else { completed.push(s); }
    }
  }
  return { steps_completed: completed, steps_remaining: remaining };
}

function isLikelyPublicNoAuth(msg?: ApiIngestMessage | null): boolean {
  if (!msg) return false;
  const authType = String(msg.auth_type || msg.api_definition?.auth_type || '').toLowerCase();
  if (['none', 'no_auth', 'public', 'anonymous'].includes(authType)) return true;
  const fields = Array.isArray(msg.required_credentials?.fields) ? msg.required_credentials.fields : null;
  if (fields && fields.length === 0) return true;

  const blob = [
    msg.required_credentials?.notes,
    msg.content,
    msg.api_definition?.description,
    ...(Array.isArray(msg.auth_setup_instructions) ? msg.auth_setup_instructions : []),
    ...(Array.isArray(msg.important_notes) ? msg.important_notes : []),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ');

  const hints = [
    'no authentication',
    'no authentication required',
    'requires no authentication',
    'no auth required',
    'without authentication',
    'public api',
    'fully public',
    'no api key required',
    'no api keys',
    'no registration required',
    'leave blank or use a placeholder',
    'anonymous access',
  ];

  return hints.some((h) => blob.includes(h));
}

export default function ApiIngestChat({ sessionId, onSessionChange, onPipelineReady, resumeApiId }: ApiIngestChatProps) {
  const [messages, setMessages] = useState<ApiIngestMessage[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPhase, setCurrentPhase] = useState<string>('idle');
  const [currentAuthType, setCurrentAuthType] = useState<string>('api_key');
  const [currentApiId, setCurrentApiId] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const lastAssistantWithContext = [...messages].reverse().find((m) => m.role === 'assistant' && (m.api_definition || m.required_credentials || m.auth_setup_instructions));
  const showPublicAutoBadge = isLikelyPublicNoAuth(lastAssistantWithContext || null) && ['bootstrapping', 'discovering', 'configuring', 'testing', 'active'].includes(currentPhase);
  const lastInteractiveMessageIndex = messages.reduce((acc, m, idx) => (
    ['select_candidate', 'confirm_selection', 'wait_for_credentials', 'start_oauth'].includes(String(m.next_action || ''))
      ? idx
      : acc
  ), -1);

  // Reset all chat state to fresh start
  const resetChat = useCallback(() => {
    setMessages([]);
    setInput('');
    setLoading(false);
    setError(null);
    setCurrentPhase('idle');
    setCurrentAuthType('api_key');
    setCurrentApiId(null);
    onSessionChange(null);
  }, [onSessionChange]);

  // Auto-scroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // On mount: reset to clean state, then resume if we have a session/api
  useEffect(() => {
    if (sessionId) {
      loadSession(sessionId);
    } else if (resumeApiId) {
      loadSessionByApiId(resumeApiId);
    } else {
      // No session — ensure we start clean (clears stale React state)
      setMessages([]);
      setLoading(false);
      setError(null);
      setCurrentPhase('idle');
      setCurrentAuthType('api_key');
      setCurrentApiId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resumeApiId]);

  /**
   * Restores the full session from backend, including rich metadata
   * (setup instructions, API definition, endpoints, notes) on the
   * last assistant message so the user sees the complete setup page.
   */
  const restoreSession = (s: any) => {
    setCurrentPhase(s.phase);
    if (s.discovered?.auth_type) setCurrentAuthType(s.discovered.auth_type);
    if (s.discovered?.api_id) setCurrentApiId(s.discovered.api_id);
    if (s.pipeline_id) onPipelineReady(s.pipeline_id);

    // Reconstruct messages from session history
    const msgs: ApiIngestMessage[] = (s.messages || []).map((m: any, idx: number) => {
      const msg: ApiIngestMessage = {
        role: m.role,
        content: m.content || '',
        timestamp: new Date(m.ts),
      };
      return msg;
    });

    // Re-attach rich metadata to the last assistant message so the user
    // can see setup instructions, credential form, API definition, etc.
    if (s.discovered && msgs.length > 0) {
      const lastAssistantIdx = msgs.reduce((acc, m, i) => (m.role === 'assistant' ? i : acc), -1);
      if (lastAssistantIdx >= 0) {
        const enriched = msgs[lastAssistantIdx];
        enriched.phase = s.phase;
        enriched.api_definition = s.discovered.api_definition;
        enriched.endpoints = s.discovered.endpoints;
        enriched.auth_type = s.discovered.auth_type;
        enriched.auth_setup_instructions = s.discovered.auth_setup_instructions;
        enriched.important_notes = s.discovered.important_notes;
        enriched.required_credentials = s.discovered.required_credentials;
        enriched.pipeline_id = s.pipeline_id;
        enriched.candidates = s.discovered.candidates || [];
        enriched.selected_candidate = s.selected_candidate || s.discovered.selected_candidate || null;
        enriched.catalog_mappings_by_endpoint = s.discovered.catalog_mappings_by_endpoint || null;
        enriched.all_mappings = s.configured?.all_mappings || null;

        // Show credential form if still awaiting
        if (s.phase === 'awaiting_credentials') {
          enriched.next_action = 'wait_for_credentials';
        }
        if (s.phase === 'awaiting_oauth') {
          enriched.next_action = 'start_oauth';
        }
        if (s.phase === 'awaiting_api_selection') {
          const hasCandidates = Array.isArray(s.discovered?.candidates) && s.discovered.candidates.length > 0;
          enriched.next_action = hasCandidates ? 'select_candidate' : 'wait_for_input';
        }
        if (s.phase === 'awaiting_confirmation') {
          enriched.next_action = 'confirm_selection';
        }
        // Show steps progress
        if (s.phase === 'awaiting_credentials') {
          const sp = stepsProgressLocal('configuring');
          enriched.steps_completed = sp.steps_completed;
          enriched.steps_remaining = sp.steps_remaining;
        } else if (s.phase === 'awaiting_api_selection') {
          const sp = stepsProgressLocal('discovering_candidates');
          enriched.steps_completed = sp.steps_completed;
          enriched.steps_remaining = sp.steps_remaining;
        } else if (s.phase === 'awaiting_confirmation') {
          const sp = stepsProgressLocal('bootstrapping');
          enriched.steps_completed = sp.steps_completed;
          enriched.steps_remaining = sp.steps_remaining;
        }
      }
    }

    setMessages(msgs);
  };

  const loadSession = async (sid: string) => {
    try {
      const resp = await apiManagerClient.get(`/cp/api-ingest/chat/session/${sid}`);
      const s = resp?.data?.session;
      if (s) restoreSession(s);
    } catch {
      // Session not found, start fresh
    }
  };

  const loadSessionByApiId = async (apiId: string) => {
    try {
      setLoading(true);
      const resp = await apiManagerClient.get(`/cp/api-ingest/chat/session-by-api/${encodeURIComponent(apiId)}`);
      const s = resp?.data?.session;
      if (s) {
        // Sync session id up to parent dashboard
        if (s.session_id) onSessionChange(s.session_id);
        restoreSession(s);
      }
    } catch {
      // No session for this API — show a message
      setMessages([{
        role: 'system',
        content: `No setup session found for API "${apiId}". You can start a new setup by describing the API above.`,
        timestamp: new Date(),
      }]);
    } finally {
      setLoading(false);
    }
  };

  // ------------------------------------------------------------------
  // Send message to chat endpoint
  // ------------------------------------------------------------------
  const sendMessage = useCallback(async (
    text: string,
    attachments?: any[],
    credentials?: any,
    retryPhase?: string,
    selection?: { candidate_id: string },
    confirmation?: { accepted: boolean },
  ) => {
    setLoading(true);
    setError(null);

    // Add user message to UI
    if (text && !credentials && !retryPhase) {
      const userMsg: ApiIngestMessage = {
        role: 'user',
        content: text,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, userMsg]);
    }

    try {
      const body: any = {
        input: text || '',
      };
      if (sessionId) body.session_id = sessionId;
      if (attachments?.length) body.attachments = attachments;
      if (credentials) body.credentials = credentials;
      if (retryPhase) body.retry_phase = retryPhase;
      if (selection) body.selection = selection;
      if (confirmation) body.confirmation = confirmation;

      const resp = await apiManagerClient.post('/cp/api-ingest/chat', body);
      const data = resp?.data;

      // Update session
      if (data?.session_id && data.session_id !== sessionId) {
        onSessionChange(data.session_id);
      }

      // If backend says poll, start polling for the async result
      if (data?.next_action === 'poll' && data?.session_id) {
        setCurrentPhase(data.phase || 'discovering_candidates');
        // Show a processing message
        setMessages((prev) => [
          ...prev,
          {
            role: 'assistant',
            content: data.response || 'Working on it...',
            timestamp: new Date(),
            phase: data.phase,
            steps_completed: data.steps_completed,
            steps_remaining: data.steps_remaining,
          },
        ]);

        // Poll session until phase changes to a terminal state (not discovering/configuring/testing)
        const pollSessionId = data.session_id;
        const pollStartPhase = data.phase || 'discovering';
        const pollInterval = 3000;
        const maxPolls = 90; // 4.5 min max (configure + test can take a while)
        let polls = 0;
        const pollAgain = () => {
          if (polls < maxPolls) {
            setTimeout(poll, pollInterval);
            return;
          }
          setLoading(false);
          setMessages((prev) => [
            ...prev,
            {
              role: 'assistant',
              content: 'Setup is taking longer than expected and is still running server-side. Reopen this API from the workspace in a minute to see the result.',
              timestamp: new Date(),
            },
          ]);
        };
        const poll = async () => {
          polls++;
          try {
            const pollResp = await apiManagerClient.get(`/cp/api-ingest/chat/session/${pollSessionId}`);
            const session = pollResp?.data?.session;
            if (!session) {
              pollAgain();
              return;
            }

            // Still in a processing phase -- keep polling
            if (['discovering_candidates', 'bootstrapping', 'discovering', 'configuring', 'testing'].includes(session.phase)) {
              pollAgain();
              return;
            }

            // Phase changed -- done polling
            setCurrentPhase(session.phase);
            if (session.discovered?.auth_type) setCurrentAuthType(session.discovered.auth_type);
            if (session.discovered?.api_id) setCurrentApiId(session.discovered.api_id);
            if (session.pipeline_id) onPipelineReady(session.pipeline_id);

            // Find the assistant message from the session
            const lastAssistant = [...(session.messages || [])].reverse().find((m: any) => m.role === 'assistant');
            const assistantContent =
              session.phase === 'error'
                ? humanizeIngestError(session.error || '')
                : (lastAssistant?.content || session.discovered?.summary || 'Discovery complete.');

            // Replace the polling placeholder message with the real result
            setMessages((prev) => {
              const updated = [...prev];
              // Remove the last assistant message if it was a polling placeholder
              const lastIdx = updated.length - 1;
              if (lastIdx >= 0 && updated[lastIdx].role === 'assistant') {
                const c = updated[lastIdx].content || '';
                if (c.includes('orking') || c.includes('earching') || c.includes('onfigur') || c.includes('esting')) {
                  updated.pop();
                }
              }

              // Determine correct next_action based on session phase
              let nextAction: string | undefined;
              if (session.phase === 'awaiting_api_selection') {
                const hasCandidates = Array.isArray(session.discovered?.candidates) && session.discovered.candidates.length > 0;
                nextAction = hasCandidates ? 'select_candidate' : 'wait_for_input';
              }
              else if (session.phase === 'awaiting_confirmation') nextAction = 'confirm_selection';
              else if (session.phase === 'awaiting_credentials') nextAction = 'wait_for_credentials';
              else if (session.phase === 'awaiting_oauth') nextAction = 'start_oauth';
              else if (session.phase === 'active') nextAction = 'complete';

              // Determine correct steps progress
              let stepsCompleted: string[] = [];
              let stepsRemaining: string[] = [];
              if (session.phase === 'awaiting_api_selection') {
                const sp = stepsProgressLocal('discovering_candidates');
                stepsCompleted = sp.steps_completed;
                stepsRemaining = sp.steps_remaining;
              } else if (session.phase === 'awaiting_confirmation') {
                const sp = stepsProgressLocal('bootstrapping');
                stepsCompleted = sp.steps_completed;
                stepsRemaining = sp.steps_remaining;
              } else if (session.phase === 'awaiting_credentials') {
                const sp = stepsProgressLocal('configuring');
                stepsCompleted = sp.steps_completed;
                stepsRemaining = sp.steps_remaining;
              } else if (session.phase === 'active') {
                stepsCompleted = Object.values(PHASE_STEPS_LOCAL).flat();
                stepsRemaining = [];
              } else if (session.phase === 'error') {
                stepsCompleted = [];
                stepsRemaining = [];
              }

              updated.push({
                role: 'assistant',
                content: assistantContent,
                timestamp: new Date(),
                phase: session.phase,
                next_action: nextAction,
                auth_type: session.discovered?.auth_type,
                api_definition: session.discovered?.api_definition,
                endpoints: session.discovered?.endpoints,
                pipeline_id: session.pipeline_id,
                steps_completed: stepsCompleted,
                steps_remaining: stepsRemaining,
                error: session.error,
                auth_setup_instructions: session.discovered?.auth_setup_instructions,
                important_notes: session.discovered?.important_notes,
                required_credentials: session.discovered?.required_credentials,
                api_id: session.discovered?.api_id,
                oauth_config: session.discovered?.oauth_config,
                candidates: session.discovered?.candidates,
                selected_candidate: session.selected_candidate || session.discovered?.selected_candidate,
                catalog_mappings_by_endpoint: session.discovered?.catalog_mappings_by_endpoint,
                all_mappings: session.configured?.all_mappings,
              });
              return updated;
            });
            setLoading(false);
          } catch {
            pollAgain();
          }
        };
        setTimeout(poll, pollInterval);
        return; // Don't setLoading(false) yet -- polling will do it
      }

      // Update phase
      if (data?.phase) setCurrentPhase(data.phase);
      if (data?.auth_type) setCurrentAuthType(data.auth_type);
      if (data?.api_id) setCurrentApiId(data.api_id);

      // Pipeline ready
      if (data?.pipeline_id) {
        onPipelineReady(data.pipeline_id);
      }

      // Add assistant message
      const assistantMsg: ApiIngestMessage = {
        role: 'assistant',
        content: data?.response || 'Processing...',
        timestamp: new Date(),
        phase: data?.phase,
        next_action: data?.next_action,
        auth_type: data?.auth_type,
        api_definition: data?.api_definition,
        endpoints: data?.endpoints,
        pipeline_id: data?.pipeline_id,
        steps_completed: data?.steps_completed,
        steps_remaining: data?.steps_remaining,
        error: data?.error,
        auth_setup_instructions: data?.auth_setup_instructions,
        important_notes: data?.important_notes,
        required_credentials: data?.required_credentials,
        api_id: data?.api_id,
        oauth_config: data?.oauth_config,
        candidates: data?.candidates,
        selected_candidate: data?.selected_candidate,
      };
      setMessages((prev) => [...prev, assistantMsg]);
      setLoading(false);
    } catch (e: any) {
      const errMsg = e?.response?.data?.error || e?.message || 'Failed to send message';
      setError(errMsg);
      setMessages((prev) => [
        ...prev,
        { role: 'system', content: humanizeIngestError(errMsg), timestamp: new Date() },
      ]);
      setLoading(false);
    }
  }, [sessionId, onSessionChange, onPipelineReady]);

  // ------------------------------------------------------------------
  // Input handling
  // ------------------------------------------------------------------
  const handleSend = () => {
    if (!input.trim() || loading) return;
    const text = input.trim();
    setInput('');

    // Detect URLs in input
    const urls = detectUrls(text);
    const attachments = urls.map((url) => ({ type: 'url', url }));

    sendMessage(text, attachments.length ? attachments : undefined);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleCredentialSubmit = (creds: any) => {
    sendMessage('', undefined, creds);
  };

  const handleSelectCandidate = (candidateId: string) => {
    sendMessage(`Select candidate ${candidateId}`, undefined, undefined, undefined, { candidate_id: candidateId });
  };

  const handleConfirmSelection = (accepted: boolean) => {
    if (accepted) {
      sendMessage('Confirm API selection', undefined, undefined, undefined, undefined, { accepted: true });
      return;
    }
    sendMessage('Choose different API', undefined, undefined, undefined, undefined, { accepted: false });
  };

  const handleRetry = (phase: string) => {
    sendMessage('', undefined, undefined, phase);
  };

  // ------------------------------------------------------------------
  // Drag & drop
  // ------------------------------------------------------------------
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length === 0) return;

    const file = files[0];
    if (file.type === 'application/pdf') {
      // Upload PDF to DAM, then send as attachment
      await handleFileUpload(file);
    } else if (file.name.endsWith('.json') || file.name.endsWith('.yaml') || file.name.endsWith('.yml')) {
      // Read OpenAPI spec file
      const text = await file.text();
      sendMessage(`Here is an OpenAPI spec file: ${file.name}`, [
        { type: 'openapi_spec', content: text },
      ]);
    }
  };

  const handleFileUpload = async (file: File) => {
    setLoading(true);
    try {
      // Upload PDF, read extracted text, send as attachment
      setMessages((prev) => [
        ...prev,
        { role: 'system', content: `Uploading ${file.name}...`, timestamp: new Date() },
      ]);

      const formData = new FormData();
      formData.append('file', file);
      formData.append('compress', 'true');

      const uploadResp = await apiManagerClient.post('/cp/dam/assets/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const assetId = uploadResp?.data?.asset_id;

      if (assetId) {
        // Extract text
        const extractResp = await apiManagerClient.post(`/cp/dam/assets/${assetId}/extract-text`, { force: false });
        const extractedText = extractResp?.data?.extracted_text || '';

        sendMessage(`I uploaded API documentation: ${file.name}`, [
          { type: 'pdf', dam_asset_id: assetId, content: extractedText },
        ]);
      } else {
        setError('PDF upload failed');
      }
    } catch (e: any) {
      setError(e?.message || 'File upload failed');
    } finally {
      setLoading(false);
    }
  };

  // ------------------------------------------------------------------
  // Render
  // ------------------------------------------------------------------
  return (
    <Paper
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 600,
        position: 'relative',
        border: isDragOver ? '2px dashed' : '1px solid',
        borderColor: isDragOver ? 'primary.main' : 'divider',
        transition: 'border-color 0.2s',
      }}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* Header */}
      <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1 }}>
        <SparklesIcon sx={{ color: 'primary.main' }} />
        <Typography variant="subtitle1" fontWeight={600}>
          API Setup Assistant
        </Typography>
        {currentPhase !== 'idle' && (
          <Chip
            label={currentPhase.replace(/_/g, ' ')}
            size="small"
            color={currentPhase === 'active' ? 'success' : currentPhase === 'error' ? 'error' : 'primary'}
            variant="outlined"
          />
        )}
        {showPublicAutoBadge && (
          <Chip
            label="Public API detected - auto-configuring"
            size="small"
            color="success"
            variant="outlined"
          />
        )}
        {/* New Session button — always visible when there are messages */}
        {messages.length > 0 && (
          <Button
            size="small"
            variant="text"
            startIcon={<RetryIcon />}
            onClick={resetChat}
            sx={{ ml: 'auto', textTransform: 'none' }}
          >
            New Session
          </Button>
        )}
      </Box>

      {/* Drag overlay */}
      {isDragOver && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 10,
            bgcolor: 'rgba(25, 118, 210, 0.08)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 1,
          }}
        >
          <Box sx={{ textAlign: 'center' }}>
            <CloudUploadIcon sx={{ fontSize: 48, color: 'primary.main' }} />
            <Typography variant="h6" color="primary">Drop API docs here</Typography>
            <Typography variant="body2" color="text.secondary">PDF, OpenAPI JSON/YAML</Typography>
          </Box>
        </Box>
      )}

      {/* Messages area */}
      <Box sx={{ flex: 1, overflow: 'auto', px: 2, py: 2 }}>
        {messages.length === 0 && !loading && (
          <QuickActions onAction={(text) => {
            setInput(text);
            // Auto-send if it's an example
            if (text.startsWith('http')) {
              sendMessage(text, [{ type: 'url', url: text }]);
            }
          }} />
        )}

        {messages.map((msg, i) => (
          <Box key={i} sx={{ mb: 2 }}>
            {/* Role indicator */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
              {msg.role === 'assistant' && <SparklesIcon sx={{ fontSize: 16, color: 'primary.main' }} />}
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                {msg.role === 'user' ? 'You' : msg.role === 'assistant' ? 'Agent' : 'System'}
              </Typography>
              <Typography variant="caption" color="text.disabled" sx={{ ml: 'auto' }}>
                {msg.timestamp.toLocaleTimeString()}
              </Typography>
            </Box>

            {/* Message content */}
            <Paper
              variant={msg.role === 'user' ? 'elevation' : 'outlined'}
              sx={{
                p: 1.5,
                bgcolor: msg.role === 'user' ? 'primary.50' : msg.role === 'system' ? 'grey.50' : 'background.paper',
                borderColor: msg.error ? 'error.main' : undefined,
              }}
            >
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {msg.content}
              </Typography>

              {/* Candidate selection cards */}
              {msg.next_action === 'select_candidate' &&
                i === lastInteractiveMessageIndex &&
                Array.isArray(msg.candidates) &&
                msg.candidates.length > 0 && (
                <Box sx={{ mt: 1.25 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                      Choose one API candidate
                    </Typography>
                    <Chip
                      size="small"
                      variant="outlined"
                      color="primary"
                      label={`${msg.candidates.length} matches`}
                    />
                  </Box>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
                      gap: 1,
                    }}
                  >
                    {msg.candidates.map((candidate) => {
                      const isSelected = msg.selected_candidate?.candidate_id === candidate.candidate_id;
                      return (
                        <Card
                          key={candidate.candidate_id}
                          variant="outlined"
                          sx={{
                            bgcolor: isSelected ? 'primary.50' : 'background.paper',
                            borderColor: isSelected ? 'primary.main' : 'divider',
                            boxShadow: isSelected ? 1 : 0,
                            transition: 'all 0.2s ease',
                          }}
                        >
                          <CardContent sx={{ py: 1.25, '&:last-child': { pb: 1.25 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
                              <Box sx={{ minWidth: 0 }}>
                                <Typography variant="subtitle2" fontWeight={700} sx={{ lineHeight: 1.25 }}>
                                  {candidate.name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {candidate.provider || 'Unknown provider'}
                                </Typography>
                              </Box>
                              {typeof candidate.confidence === 'number' && (
                                <Chip
                                  size="small"
                                  variant="outlined"
                                  color={candidate.confidence >= 0.75 ? 'success' : candidate.confidence >= 0.5 ? 'warning' : 'default'}
                                  label={`${(candidate.confidence * 100).toFixed(0)}%`}
                                />
                              )}
                            </Box>

                            <Stack direction="row" spacing={0.75} sx={{ mt: 1, flexWrap: 'wrap', rowGap: 0.75 }}>
                              <Chip label={candidate.auth_type || 'unknown auth'} size="small" variant="outlined" />
                              {isSelected && <Chip label="Selected" size="small" color="primary" />}
                            </Stack>

                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: 'block', mt: 1, minHeight: 32 }}
                            >
                              {candidate.why || 'Likely match based on your request.'}
                            </Typography>

                            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                              {candidate.docs_url && (
                                <Button
                                  size="small"
                                  variant="text"
                                  startIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                                  href={candidate.docs_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  sx={{ textTransform: 'none', px: 0.5, minWidth: 0 }}
                                >
                                  Docs
                                </Button>
                              )}
                              {candidate.spec_url && (
                                <Button
                                  size="small"
                                  variant="text"
                                  startIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                                  href={candidate.spec_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  sx={{ textTransform: 'none', px: 0.5, minWidth: 0 }}
                                >
                                  OpenAPI
                                </Button>
                              )}
                            </Stack>

                            <Button
                              size="small"
                              fullWidth
                              variant={isSelected ? 'contained' : 'outlined'}
                              onClick={() => handleSelectCandidate(candidate.candidate_id)}
                              disabled={loading}
                              sx={{ mt: 1.25, textTransform: 'none' }}
                            >
                              {isSelected ? 'Selected' : 'Select API'}
                            </Button>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </Box>
                </Box>
              )}

              {/* Selection confirmation prompt */}
              {msg.next_action === 'confirm_selection' && i === lastInteractiveMessageIndex && (
                <Card variant="outlined" sx={{ mt: 1.25, bgcolor: 'primary.50', borderColor: 'primary.main' }}>
                  <CardContent sx={{ py: 1.25, '&:last-child': { pb: 1.25 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                      <Typography variant="subtitle2" fontWeight={700}>
                        Confirm API creation
                      </Typography>
                      <Chip size="small" color="primary" label="Ready to create" />
                    </Box>
                    {msg.selected_candidate && (
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="body2">
                          <strong>{msg.selected_candidate.name}</strong>
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {msg.selected_candidate.provider || 'Unknown provider'} · Auth: {msg.selected_candidate.auth_type || 'unknown'}
                        </Typography>
                      </Box>
                    )}
                    <Stack direction="row" spacing={1} sx={{ mt: 1.25 }}>
                      <Button
                        size="small"
                        variant="contained"
                        color="primary"
                        onClick={() => handleConfirmSelection(true)}
                        disabled={loading}
                        sx={{ textTransform: 'none' }}
                      >
                        Confirm API
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        color="inherit"
                        onClick={() => handleConfirmSelection(false)}
                        disabled={loading}
                        sx={{ textTransform: 'none' }}
                      >
                        Choose Different
                      </Button>
                    </Stack>
                  </CardContent>
                </Card>
              )}

              {/* Workflow progress */}
              {msg.steps_completed && msg.steps_remaining && (
                <WorkflowProgress completed={msg.steps_completed} remaining={msg.steps_remaining} />
              )}

              {/* API Definition card */}
              {msg.api_definition && (
                <Card variant="outlined" sx={{ mt: 1, bgcolor: 'success.50' }}>
                  <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                    <Typography variant="caption" fontWeight={600}>
                      Discovered API: {msg.api_definition.api_name}
                    </Typography>
                    <Typography variant="caption" display="block" color="text.secondary">
                      Provider: {msg.api_definition.provider} | Auth: {msg.api_definition.auth_type}
                      {msg.endpoints?.length ? ` | ${msg.endpoints.length} endpoints` : ''}
                    </Typography>
                    {msg.api_definition.documentation_url && (
                      <Typography variant="caption" display="block" color="primary.main">
                        Docs: <a href={msg.api_definition.documentation_url} target="_blank" rel="noopener noreferrer">{msg.api_definition.documentation_url}</a>
                      </Typography>
                    )}
                    {msg.endpoints && msg.endpoints.length > 0 && (
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="caption" fontWeight={600} display="block">
                          Discovered endpoints & fields
                        </Typography>
                        {msg.endpoints.slice(0, 8).map((ep: any) => (
                          <Typography key={ep.endpoint_id || ep.path} variant="caption" display="block" color="text.secondary" sx={{ pl: 0.5 }}>
                            {ep.method || 'GET'} {ep.path}
                            {ep.response_schema?.key_fields?.length
                              ? ` — fields: ${ep.response_schema.key_fields.slice(0, 6).join(', ')}`
                              : ''}
                          </Typography>
                        ))}
                        {msg.endpoints.length > 8 && (
                          <Typography variant="caption" color="text.disabled">
                            +{msg.endpoints.length - 8} more endpoints
                          </Typography>
                        )}
                      </Box>
                    )}
                  </CardContent>
                </Card>
              )}

              {(msg.all_mappings?.length || msg.catalog_mappings_by_endpoint?.length) && (
                <Card variant="outlined" sx={{ mt: 1, bgcolor: 'grey.50' }}>
                  <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                    <Typography variant="caption" fontWeight={600}>
                      Canonical field mappings
                    </Typography>
                    {(msg.all_mappings || msg.catalog_mappings_by_endpoint || []).slice(0, 3).map((group: any) => (
                      <Box key={group.endpoint_id} sx={{ mt: 0.5 }}>
                        <Typography variant="caption" fontWeight={600} display="block">
                          {group.endpoint_id}
                        </Typography>
                        {(group.mappings || []).slice(0, 6).map((m: any) => (
                          <Typography key={`${group.endpoint_id}-${m.external_field}`} variant="caption" display="block" color="text.secondary" sx={{ pl: 0.5 }}>
                            {m.external_field} → {m.iku_field}
                            {m.confirmed === false ? ' (hypothesis)' : ''}
                          </Typography>
                        ))}
                      </Box>
                    ))}
                  </CardContent>
                </Card>
              )}

              {/* Auth setup instructions */}
              {msg.auth_setup_instructions && msg.auth_setup_instructions.length > 0 && (
                <Card variant="outlined" sx={{ mt: 1, bgcolor: 'info.50', borderColor: 'info.light' }}>
                  <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                    <Typography variant="caption" fontWeight={600} color="info.dark">
                      How to get your credentials:
                    </Typography>
                    {msg.auth_setup_instructions.map((step: string, idx: number) => (
                      <Typography key={idx} variant="caption" display="block" color="text.secondary" sx={{ pl: 1, mt: 0.25 }}>
                        {step}
                      </Typography>
                    ))}
                  </CardContent>
                </Card>
              )}

              {/* Important notes */}
              {msg.important_notes && msg.important_notes.length > 0 && (
                <Card variant="outlined" sx={{ mt: 1, bgcolor: 'warning.50', borderColor: 'warning.light' }}>
                  <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                    <Typography variant="caption" fontWeight={600} color="warning.dark">
                      Important notes:
                    </Typography>
                    {msg.important_notes.map((note: string, idx: number) => (
                      <Typography key={idx} variant="caption" display="block" color="text.secondary" sx={{ pl: 1, mt: 0.25 }}>
                        - {note}
                      </Typography>
                    ))}
                  </CardContent>
                </Card>
              )}

              {/* Pipeline ready */}
              {msg.pipeline_id && (
                <Alert severity="success" sx={{ mt: 1 }}>
                  Pipeline active: {msg.pipeline_id}
                </Alert>
              )}

              {/* Error with retry */}
              {msg.error && msg.phase === 'error' && (
                <Box sx={{ mt: 1 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    startIcon={<RetryIcon />}
                    onClick={() => handleRetry('discovering')}
                  >
                    Retry
                  </Button>
                </Box>
              )}
            </Paper>

            {/* Credential form (shown after the message that requests credentials or OAuth) */}
            {(msg.next_action === 'wait_for_credentials' || msg.next_action === 'start_oauth') && i === lastInteractiveMessageIndex && (
              <CredentialForm
                authType={msg.auth_type || currentAuthType}
                onSubmit={handleCredentialSubmit}
                loading={loading}
                apiId={msg.api_id || currentApiId}
                oauthConfig={msg.oauth_config}
                sessionId={sessionId}
                onOAuthComplete={() => {
                  // Reload the session to get the updated messages
                  if (sessionId) loadSession(sessionId);
                }}
              />
            )}
          </Box>
        ))}

        {/* Loading indicator */}
        {loading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <CircularProgress size={18} />
            <Typography variant="body2" color="text.secondary">
              Agent is working...
            </Typography>
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <div ref={messagesEndRef} />
      </Box>

      {/* Input area */}
      <Box sx={{ px: 2, py: 1.5, borderTop: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf,.json,.yaml,.yml"
            hidden
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) handleFileUpload(f);
              e.target.value = '';
            }}
          />
          <IconButton
            size="small"
            onClick={() => fileInputRef.current?.click()}
            disabled={loading}
            title="Upload file"
          >
            <UploadFileIcon />
          </IconButton>
          <TextField
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={
              currentPhase === 'awaiting_credentials'
                ? 'Provide credentials above, or type a question...'
                : currentPhase === 'awaiting_api_selection'
                  ? 'Select a candidate above, or ask a clarifying question...'
                  : currentPhase === 'awaiting_confirmation'
                    ? 'Confirm the selected API, or ask a question...'
                : 'Describe the API you want to set up, paste a URL, or drop a file...'
            }
            fullWidth
            size="small"
            multiline
            maxRows={4}
            disabled={loading}
          />
          <IconButton
            color="primary"
            onClick={handleSend}
            disabled={loading || !input.trim()}
            title="Send"
          >
            {loading ? <CircularProgress size={20} /> : <SendIcon />}
          </IconButton>
        </Box>
        <Typography variant="caption" color="text.disabled" sx={{ mt: 0.5, display: 'block' }}>
          Press Enter to send | Drop PDF/OpenAPI files | Paste URLs
        </Typography>
      </Box>
    </Paper>
  );
}
